import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { ArrowLeft } from "lucide-react"

import FeaturesAnimals from "@/assets/img/aura/features-animals.webp"
import TopGlow from "@/assets/img/soulmate/top-glow.png"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { StepComponentProps } from "./step-contract"

const CHIP_DURATION_MS = 600
const CHIP_STAGGER_MS = 500
/** Figma artboard for the animal + chips (LPR_Quiz_Aura_Step2.1). */
const ART_SIZE = 414

const CHIPS = [
  { emoji: "✨", label: "Energy", className: "left-[40px] top-[40px]", from: "left" },
  { emoji: "🔮", label: "Intuition", className: "left-[263px] top-[72px]", from: "right" },
  { emoji: "🌿", label: "Connection", className: "bottom-[72px] left-[48px]", from: "left" },
  { emoji: "🌙", label: "Inner self", className: "left-[270px] top-[314px]", from: "right" },
] as const

/**
 * Aura Scan Step2.1 interstitial: spirit-animal artwork with floating feature
 * chips (Energy / Intuition / Connection / Inner self) that slide in one by
 * one, then Continue.
 */
export function AuraFeaturesStep({ step, onAdvance, onBack }: StepComponentProps) {
  const [showCta, setShowCta] = useState(false)

  useEffect(() => {
    const delay = (CHIPS.length - 1) * CHIP_STAGGER_MS + CHIP_DURATION_MS
    const timer = window.setTimeout(() => setShowCta(true), delay)
    return () => window.clearTimeout(timer)
  }, [])

  return (
    <div
      className="relative flex flex-1 flex-col overflow-hidden bg-dark-base text-purple-light"
      data-slot="aura-features-step"
    >
      <img
        src={TopGlow}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 h-[240px] w-full object-cover"
      />

      <div className="relative flex flex-1 flex-col">
        <div className="flex h-14 items-center px-4">
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            className="-ml-2 flex size-10 items-center justify-center rounded-full text-purple-light transition-colors hover:bg-purple-light/10"
          >
            <ArrowLeft className="size-6" />
          </button>
        </div>

        <h1 className="px-4 text-center font-display text-2xl font-medium text-purple-light">
          {step.title}
        </h1>

        <div className="flex flex-1 items-center justify-center px-0">
          <AuraFeaturesArt />
        </div>

        <div className="px-4 pt-2 pb-safe">
          <Button
            variant="gradient"
            size="xl"
            disabled={!showCta}
            onClick={onAdvance}
            className={cn(
              showCta
                ? "animate-in fade-in duration-500 fill-mode-both"
                : "invisible pointer-events-none",
            )}
          >
            {step.cta ?? "Continue"}
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Animal art + chips laid out at the Figma 414px size, then scaled to the quiz column. */
function AuraFeaturesArt() {
  const outerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useLayoutEffect(() => {
    const outer = outerRef.current
    if (!outer) return
    const update = () => setScale(outer.clientWidth / ART_SIZE)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(outer)
    return () => ro.disconnect()
  }, [])

  return (
    <div
      ref={outerRef}
      className="relative w-full shrink-0 overflow-hidden"
      style={{ height: ART_SIZE * scale }}
    >
      <div
        className="absolute left-0 top-0 size-[414px] origin-top-left"
        style={{ transform: `scale(${scale})` }}
      >
        <img
          src={FeaturesAnimals}
          alt=""
          className="absolute inset-0 size-full object-cover mix-blend-lighten"
        />
        {CHIPS.map((chip, index) => (
          <div
            key={chip.label}
            className={cn(
              "absolute flex flex-col items-center justify-center gap-2 rounded-2xl px-4 py-2 backdrop-blur-[10px]",
              "animate-in fade-in fill-mode-both",
              chip.from === "left" ? "slide-in-from-left-16" : "slide-in-from-right-16",
              chip.className,
            )}
            style={{
              animationDuration: `${CHIP_DURATION_MS}ms`,
              animationDelay: `${index * CHIP_STAGGER_MS}ms`,
              backgroundImage:
                "linear-gradient(180deg, rgba(223,203,245,0.1) 0%, rgba(149,101,253,0.1) 100%), linear-gradient(90deg, rgba(13,8,28,0.3) 0%, rgba(13,8,28,0.3) 100%)",
            }}
          >
            <span className="text-[28px] leading-none">{chip.emoji}</span>
            <span className="text-xs font-semibold uppercase text-purple-light">
              {chip.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
